class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    // In case the section is already rendered (theme editor / server render), initialize swipers.
    queueMicrotask(() => this.#initSwipers(this));
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#initSwipers(this);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }

  /**
   * Initializes TRGT Swipers inside product recommendations (Swiper markup is injected via innerHTML).
   * Scoped to [data-trgt-pr-swiper] containers only.
   * @param {ParentNode} scope
   */
  #initSwipers(scope) {
    if (!window.Swiper) return;
    const roots = Array.from(scope.querySelectorAll?.('[data-trgt-pr-swiper]') || []);

    roots.forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      if (root.dataset.swiperInitialized === 'true') return;

      const swiperEl = root.querySelector('[data-swiper]');
      const paginationEl = root.querySelector('[data-pagination]');
      const prevEl = root.querySelector('[data-prev]');
      const nextEl = root.querySelector('[data-next]');
      const controlsEl = root.querySelector('.trgt-pr__controls');
      if (!(swiperEl instanceof HTMLElement) || !(paginationEl instanceof HTMLElement)) return;

      const columns = Math.max(1, parseInt(root.dataset.columns || '4', 10));
      const tabletColumns = Math.max(1, parseInt(root.dataset.tabletColumns || String(columns), 10));
      const mobileColumns = Math.max(1, parseInt(root.dataset.mobileColumns || '2', 10));
      const gap = Math.max(0, parseInt(root.dataset.gap || '8', 10));
      const mobileGap = Math.max(0, parseInt(root.dataset.gapMobile || String(gap), 10));
      const desktopPeek = columns === 4 ? 0.2 : 0;

      let pageSlideIndexes = [0];
      let lastDotCount = 1;

      const renderDots = (count) => {
        const dots = Array.from({ length: count })
          .map(
            (_, i) =>
              `<button type="button" class="trgt-pr__dot${i === 0 ? ' is-active' : ''}" data-dot="${i}" aria-label="Go to slide ${
                i + 1
              }"${i === 0 ? ' aria-current="true"' : ''}></button>`
          )
          .join('');
        paginationEl.innerHTML = dots;
      };

      const setActiveDot = (i) => {
        const dots = Array.from(paginationEl.querySelectorAll('[data-dot]'));
        dots.forEach((dot) => {
          const idx = parseInt(dot.getAttribute('data-dot') || '0', 10);
          const active = idx === i;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const computePages = (sw) => {
        const snapGrid = sw?.snapGrid || [];
        const slidesGrid = sw?.slidesGrid || [];
        if (!snapGrid.length || !slidesGrid.length) return [0];

        const pages = [];
        for (let s = 0; s < snapGrid.length; s++) {
          const snapPos = snapGrid[s];
          let bestIdx = 0;
          let bestDist = Infinity;
          for (let i = 0; i < slidesGrid.length; i++) {
            const dist = Math.abs(slidesGrid[i] - snapPos);
            if (dist < bestDist) {
              bestDist = dist;
              bestIdx = i;
            }
          }
          if (pages.length === 0 || pages[pages.length - 1] !== bestIdx) pages.push(bestIdx);
        }
        return pages.length ? pages : [0];
      };

      const syncDots = (sw) => {
        pageSlideIndexes = computePages(sw);
        const count = pageSlideIndexes.length || 1;
        if (count !== lastDotCount) {
          lastDotCount = count;
          renderDots(count);
        }
        const active = Math.max(0, Math.min(count - 1, sw?.snapIndex || 0));
        setActiveDot(active);

        // Hide pagination/arrows when there is only one page.
        if (controlsEl instanceof HTMLElement) {
          controlsEl.hidden = count <= 1;
        }
      };

      const swiper = new window.Swiper(swiperEl, {
        speed: 400,
        spaceBetween: gap,
        centeredSlides: false,
        watchOverflow: true,
        keyboard: { enabled: true, onlyInViewport: true },
        navigation: prevEl && nextEl ? { prevEl, nextEl } : undefined,
        breakpoints: {
          0: { slidesPerView: mobileColumns, spaceBetween: mobileGap },
          768: { slidesPerView: tabletColumns, spaceBetween: gap },
          992: { slidesPerView: columns + desktopPeek, spaceBetween: gap }
        },
        on: {
          afterInit: (sw) => syncDots(sw),
          slideChange: (sw) => setActiveDot(Math.max(0, Math.min(lastDotCount - 1, sw.snapIndex || 0))),
          resize: (sw) => syncDots(sw),
          breakpoint: (sw) => syncDots(sw)
        }
      });

      paginationEl.addEventListener('click', (e) => {
        const btn = e.target instanceof Element ? e.target.closest('[data-dot]') : null;
        if (!btn) return;
        const idx = parseInt(btn.getAttribute('data-dot') || '0', 10);
        const slideIndex = pageSlideIndexes[idx];
        swiper.slideTo(typeof slideIndex === 'number' ? slideIndex : idx, 400);
      });

      // Safety: initial sync
      syncDots(swiper);

      root.dataset.swiperInitialized = 'true';
    });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
